import * as React from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '../organisms/AppBar';
import Drawer from '../organisms/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Copyright from '../molecules/Copyright';
import Record from '../molecules/Record';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from '../../store/store';
import { v2api } from '../../api/axios';
import Log from '../../lib/log';
import { FormControl, InputLabel, MenuItem, Switch } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import CommonDialog from '../atoms/CommonDialog';
import Spinner from '../atoms/Spinner';
import { useDispatch } from 'react-redux';
import { setUser } from '../../store/loginUserSlice';
import { incomingsType } from '../../types/Types';

// ログの初期化
const log = new Log('Incomings');

function IncomingsContent() {
  log.putLog('info', 'Incomings started.');

  // ストアからユーザー情報を取得
  const user = useSelector((state) => state.loginUser.user);
  const dispatch = useDispatch();

  const [incomings, setIncomings] = useState<incomingsType[]>([
    {
      caller_number: '',
      id: '',
      incoming_date: '',
      message: '',
      recording_duration: 0,
      recording_s3_id: '',
      deleted_date: '',
    },
  ]);

  // incomingsの取得
  useEffect(() => {
    (async () => {
      // スピナー表示
      setLoading(true);
      // incomingsを取得する
      const postData = {
        userId: user.id,
        token: user.access_token,
        offset: 0,
      };
      const res = await v2api.post('/get-incomings', postData);
      if (res.data.statusCode === 200) {
        // 取得成功
        const body = JSON.parse(res.data.body);
        setIncomings(body);
        log.putLog('debug', `incomings ${body.length} got.`);
      } else {
        // エラー
        log.putLog(
          'error',
          `get-incomings error. statusCode: ${res.data.statusCode}`,
        );
        // ストアを初期化
        dispatch(setUser(''));
      }
      // スピナー非表示
      setLoading(false);
    })();
  }, [user, dispatch]);

  // ダイヤログ
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  // レコード削除
  const handleDelete = (id: string) => {
    setDeleteId(id);
    setDialogOpen(true);
  };

  // 削除実行
  const handleConfirm = async (result: boolean) => {
    if (result) {
      // スピナー表示
      setLoading(true);
      // incomingsを削除（更新）する
      const postData = {
        id: deleteId,
        userId: user.id,
        token: user.access_token,
      };
      const res = await v2api.post('/delete-incoming', postData);
      if (res.data.statusCode === 200) {
        // 更新成功
        setIncomings(
          incomings.filter((incoming) => {
            return incoming.id !== deleteId;
          }),
        );
      } else {
        // エラー
        log.putLog(
          'error',
          `delete-incoming error. statusCode: ${res.data.statusCode}`,
        );
      }
      // スピナー非表示
      setLoading(false);
    }
    setDialogOpen(false);
    setDeleteId('');
  };

  // Spinner
  const [loading, setLoading] = useState(true);

  /** *************
   * Pagination関連
   ****************/
  const [itemPerPage, setItemPerPage] = useState(10); // 初期設定時は５件/ページ
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [recordedOnly, setRecordedOnly] = useState(true); // 録音データのみ表示

  // 表示データ抽出
  const [records, setRecords] = useState(incomings);
  useLayoutEffect(() => {
    // スピナー表示
    setLoading(true);
    // 削除されていないデータのみを抽出
    let selectedRecords = incomings.filter(
      (incoming) => !incoming.deleted_date,
    );
    if (recordedOnly)
      // 録音されているデータのみを抽出
      selectedRecords = selectedRecords.filter(
        (incoming) => incoming.recording_s3_id,
      );
    // 最終ページを計算
    setMaxPage(Math.ceil(selectedRecords.length / itemPerPage));
    // 今、表示が必要なデータを抽出
    selectedRecords = selectedRecords.slice(
      (currentPage - 1) * itemPerPage,
      currentPage * itemPerPage,
    );
    setRecords(selectedRecords);
    // スピナー非表示
    setLoading(false);
  }, [incomings, currentPage, itemPerPage, recordedOnly]);

  // ページ遷移
  const handlePageChange = (e: React.ChangeEvent<unknown>, page: number) => {
    log.putLog('debug', `page: ${page}`);
    setCurrentPage(page);
  };

  // 表示データ切り替え
  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecordedOnly(e.target.checked);
  };

  // 表示件数切り替え
  const handleItemChange = (e: SelectChangeEvent) => {
    setItemPerPage(Number(e.target.value));
    setCurrentPage(1); // 先頭から表示しなおし
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar title='受信ボックス' />
      <Drawer />
      <CommonDialog
        openDialog={dialogOpen}
        title={'削除の確認'}
        message={'このデータを削除しますか？'}
        handleConfirm={(result) => handleConfirm(result)}
      />
      <Box
        component='main'
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid
              container
              spacing={0}
              direction='column'
              alignItems='flex-end'
              justifyContent='flex-end'
              padding={0}
            >
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={recordedOnly}
                        onChange={handleSelectChange}
                      />
                    }
                    label='録音データのみ'
                  />
                </FormGroup>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Spinner loading={loading} />
              {records.map((incoming) => {
                return (
                  <Record
                    key={incoming.id}
                    id={incoming.id}
                    recordId={incoming.recording_s3_id}
                    message={incoming.message}
                    incomingDate={incoming.incoming_date}
                    duration={incoming.recording_duration}
                    fromNumber={incoming.caller_number}
                    handleDelete={(id) => handleDelete(id)}
                  />
                );
              })}
            </Grid>
            <Grid
              container
              spacing={0}
              direction='column'
              alignItems='center'
              justifyContent='center'
              paddingTop={2}
            >
              <Grid item xs={12}>
                <Stack spacing={2}>
                  <Pagination
                    count={maxPage}
                    shape='rounded'
                    onChange={handlePageChange}
                  />
                </Stack>
                <FormControl sx={{ m: 1, minWidth: 100 }} size='small'>
                  <InputLabel id='item-select-label'>表示行数</InputLabel>
                  <Select
                    labelId='item-select-label'
                    id='item-select'
                    value={itemPerPage.toString()}
                    label='表示行数'
                    onChange={handleItemChange}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item></Grid>
            </Grid>
          </Grid>
          <Copyright sx={{ pt: 4 }} />
        </Container>
      </Box>
    </Box>
  );
}

export default function Incomings() {
  return <IncomingsContent />;
}
